import React, { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";

import { useIsMobile } from "@/app/redux/features/device/selectors";
import { usePassWinners, formatMonthYear } from "@/app/hooks/usePassWinners";

import "swiper/css";
import "swiper/css/navigation";

function Winners() {
  const { winners } = usePassWinners();
  const isMobile = useIsMobile();
  const prevButtonRef = useRef<HTMLDivElement>(null);
  const nextButtonRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div className={`font-jost mb-6 lg:mb-16 passWinners ${winners.length === 0 ? "hidden" : ""}`}>
        <div
          className={
            "text-center text-dark-1 text-lg font-semibold leading-6.5 mb-6 lg:mb-10 lg:text-[28px] lg:leading-[22px]"
          }>
          Meet our BOTB Pass Winners
        </div>
        <div className={"grid max-lg:-mx-4 relative"}>
          {/*navigation*/}
          {!isMobile && (
            <div className={"absolute right-0 -top-8 h-6 w-14 z-10 flex items-center justify-between gap-2"}>
              <div
                ref={prevButtonRef}
                className={"swiper-button-prev-custom size-6 p-1 bg-white rounded cursor-pointer"}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className={"size-4"}>
                  <path
                    d="M10.4689 13.1388C10.5939 13.0138 10.6641 12.8442 10.6641 12.6675C10.6641 12.4907 10.5939 12.3211 10.4689 12.1961L6.27353 8.0008L10.4689 3.80543C10.5903 3.67973 10.6575 3.51133 10.656 3.33653C10.6545 3.16173 10.5844 2.99453 10.4608 2.87093C10.3371 2.74733 10.1699 2.67723 9.99513 2.67573C9.82034 2.67413 9.65194 2.74133 9.5262 2.86283L4.85958 7.52947C4.73458 7.65449 4.66438 7.82402 4.66438 8.0008C4.66438 8.17758 4.73458 8.34711 4.85958 8.47213L9.5262 13.1388C9.65122 13.2638 9.82076 13.334 9.99753 13.334C10.1743 13.334 10.3439 13.2638 10.4689 13.1388Z"
                    fill="#454553"
                  />
                </svg>
              </div>
              <div
                ref={nextButtonRef}
                className={"swiper-button-next-custom size-6 p-1 bg-white rounded cursor-pointer"}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className={"size-4"}>
                  <path
                    d="M5.53113 2.86121C5.40615 2.98623 5.33594 3.15577 5.33594 3.33254C5.33594 3.50932 5.40615 3.67886 5.53113 3.80388L9.72647 7.99921L5.53113 12.1946C5.40969 12.3203 5.3425 12.4887 5.34402 12.6635C5.34554 12.8383 5.41565 13.0055 5.53925 13.1291C5.66286 13.2527 5.83007 13.3228 6.00487 13.3243C6.17966 13.3259 6.34806 13.2587 6.4738 13.1372L11.1404 8.47054C11.2654 8.34552 11.3356 8.17599 11.3356 7.99921C11.3356 7.82243 11.2654 7.6529 11.1404 7.52788L6.4738 2.86121C6.34878 2.73623 6.17924 2.66602 6.00247 2.66602C5.82569 2.66602 5.65615 2.73623 5.53113 2.86121Z"
                    fill="#454553"
                  />
                </svg>
              </div>
            </div>
          )}

          {/*SWIPER START*/}
          <Swiper
            onInit={swiper => {
              setTimeout(() => {
                swiper.update();
                swiper.updateSlides();
              }, 500);
            }}
            className={"w-full !pt-4 max-lg:!pb-6"}
            navigation={{
              enabled: !isMobile,
              prevEl: prevButtonRef.current,
              nextEl: nextButtonRef.current,
              disabledClass: "disabledSwiperNav",
            }}
            pagination={
              isMobile && winners.length <= 15
                ? {
                    enabled: isMobile,
                    clickable: true,
                    // dynamicBullets: true,
                    // dynamicMainBullets: 3,
                    bulletClass: "bullet-bg",
                    horizontalClass: "paginationWinners",
                  }
                : false
            }
            modules={[Navigation, Pagination]}
            slidesOffsetBefore={isMobile ? 16 : 0}
            slidesOffsetAfter={isMobile ? 16 : 0}
            slidesPerView={isMobile ? 1.2 : 3}
            spaceBetween={isMobile ? 10 : 16}>
            {winners.map((winner, index) => (
              <SwiperSlide key={index} className={"!h-auto"}>
                <div className={"rounded-lg relative px-4 pt-4 pb-5 bg-white h-full"}>
                  <img
                    src={"/assets/images/botb-pass/winner-badge.png"}
                    alt="botb-pass winner"
                    className={"absolute -top-4 left-4 z-20 size-12 lg:left-0 lg:size-20"}
                  />
                  <div className={"relative mb-4"}>
                    <img
                      src={winner.Photo}
                      className={"max-h-20 w-full object-cover rounded-lg lg:max-h-[122px]"}
                      alt="Winner Prize"
                    />
                    {winner.PrizeImages.length > 0 && (
                      <img
                        src={winner.PrizeImages[0]}
                        alt="Winner Prize Small"
                        className={
                          "absolute object-cover rounded-full !border !border-primary bg-gray-5 bottom-0 -right-4 z-10 w-[94px] h-[97px] lg:w-[134px] lg:h-[138px]"
                        }
                      />
                    )}
                  </div>
                  <div
                    className={
                      "border-b border-black/8 pb-3 text-primary mb-4 text-lg font-semibold leading-6 truncate"
                    }>
                    {winner.Prize}
                  </div>
                  <div>
                    <div className={"mb-1"}>{winner.Name.length > 0 ? winner.Name : "\u00A0"}</div>
                    <div className={"flex items-center mb-6 gap-1.5"}>
                      <div className={"text-[11px] leading-none text-secondary font-medium lg:text-sm lg:leading-5"}>
                        BOTB Pass Member
                      </div>
                      <div className={"size-2 bg-[#D9D9D9] rounded-full"} />
                      <div className={"text-[11px] leading-none text-secondary font-medium lg:text-sm lg:leading-5"}>
                        {formatMonthYear(winner.Date.MY)}
                      </div>
                    </div>
                    <div className={"relative"}>
                      <div className={"absolute left-0 top-0"}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="91"
                          height="46"
                          viewBox="0 0 91 46"
                          fill="none"
                          className={"w-[91px]"}>
                          <path
                            d="M33.4007 46L0 45.8296L25.3914 0H49.7603L33.4007 46ZM74.6405 46L41.4101 45.8296L66.8015 0H91L74.6405 46Z"
                            fill="black"
                            fill-opacity="0.05"
                          />
                        </svg>
                      </div>
                      <div
                        className={"italic text-base font-normal leading-7 text-gray-1 px-4"}
                        dangerouslySetInnerHTML={{ __html: winner.StoryPreview }}></div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          {/*SWIPER END*/}
        </div>
      </div>
    </>
  );
}

export default Winners;
